import { apiClient } from './axios';

export interface CreatePetRequest {
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
  breed: string;
  dateOfBirth?: string;
  approximateAge?: string;
  gender: 'male' | 'female';
  neuteredSpayed?: boolean;
  color?: string;
  microchipId?: string;
  country?: string;
  city?: string;
  motherDid?: string;
  fatherDid?: string;

  // Health & Medical
  medicalConditions?: string;
  allergies?: string;
  currentMedications?: string;
  dietaryRequirements?: string;
  vetClinic?: string;

  // Lifestyle & Care
  temperament?: string[];
  groomingTolerance?: string;
  trainingLevel?: string;
  exerciseNeeds?: string;
  boardingSuitability?: string;

  // Consent settings
  shareHealthData?: boolean;
  shareGroomingNotes?: boolean;
  emergencyAccess?: boolean;

  // Photo
  imageUrl?: string;
  photoKey?: string;

  // Secondary Owners / Caretakers
  secondaryOwnerEmails?: string[];
  secondaryOwnerRoles?: Record<string, 'CO_OWNER' | 'CARETAKER'>;

  // Birth / Litter – ownership status for puppies
  ownershipStatus?: 'active' | 'pending_transfer';
}

export interface MicrochipDataResponse {
  chipNumber: string;
  standard?: 'ISO' | 'AVID' | 'Other';
  implantDate?: string;
  implantLocation?: string;
  linkToDID: boolean;
  visibility: 'only-me' | 'professionals' | 'emergency';
}

export type DidStatus = 'active' | 'revoked';
export type LostStatus = 'active' | 'lost' | 'recovered';

export interface PetResponse {
  id: string;
  did?: string;
  didStatus?: DidStatus;
  didRevokedAt?: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
  breed: string;
  dateOfBirth?: string;
  gender: string;
  color?: string;
  microchipId?: string;
  microchip?: MicrochipDataResponse | null;
  country?: string;
  city?: string;
  parents?: {
    mother?: string;
    father?: string;
  };
  maternalGrandmother?: string;
  maternalGrandfather?: string;
  paternalGrandmother?: string;
  paternalGrandfather?: string;
  siblings?: string[];
  aiEnabled?: boolean;
  aiSettings?: {
    allowProfessionalView: boolean;
  };
  imageUrl?: string;
  photoKey?: string;
  createdAt: string;
  updatedAt: string;
  // DID Permissions
  didPermissionsViewData?: string[];
  didPermissionsAmendFields?: string[];
  didPermissionsIssueCredentials?: string[];
  didPermissionsTimeBound?: boolean;
  didPermissionsExpiryDate?: string;
  // Secondary Owners / Caretakers
  secondaryOwnerEmails?: string[];
  isCaretaker?: boolean;
  // Ownership Overview (from getPetById)
  ownershipOverview?: OwnershipOverview;
  currentUserRole?: 'PRIMARY_OWNER' | 'CO_OWNER' | 'CARETAKER';
  // Lost Pet Status
  lostStatus?: LostStatus;
  lostAt?: string;
  lostDescription?: string;
  lostLocation?: string;
  recoveredAt?: string;
  // Deceased Status
  isDeceased?: boolean;
  deceasedAt?: string;

  // Birth / Litter – ownership status for puppies
  ownershipStatus?: 'active' | 'pending_transfer';
}

export interface OwnershipOverview {
  primaryOwner: { email: string; fullName: string };
  secondaryOwners: {
    email: string;
    role: 'CO_OWNER' | 'CARETAKER';
    status: 'PENDING' | 'ACTIVE';
  }[];
}

export interface CreatePetResponse {
  message: string;
  pet: {
    id: string;
    name: string;
    species: string;
    breed: string;
    gender: string;
    country?: string;
    city?: string;
    imageUrl?: string;
    photoKey?: string;
    createdAt: string;
  };
  reward?: {
    amount: number;
    message: string;
  };
}

export interface GetPetsResponse {
  pets: PetResponse[];
}

export interface UpdatePetDidResponse {
  message: string;
  pet: {
    id: string;
    did: string;
    name: string;
  };
}

export type FamilyRelation =
  | 'mother'
  | 'father'
  | 'sibling'
  | 'maternalGrandmother'
  | 'maternalGrandfather'
  | 'paternalGrandmother'
  | 'paternalGrandfather';

export interface AddFamilyMemberRequest {
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
  relation: FamilyRelation;
  did?: string;
  gender?: 'male' | 'female';
  breed?: string;
  approximateAge?: string;
  color?: string;
  breederName?: string;
  notes?: string;
}

export interface SearchPetsRequest {
  nameOrBreed?: string;
  microchipId?: string;
  breederReference?: string;
  did?: string;
}

export interface SearchPetsResponse {
  pets: {
    id: string;
    did: string;
    name: string;
    species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
    breed: string;
    gender: 'male' | 'female';
    imageUrl?: string;
    ownerClerkId?: string;
    microchipId?: string;
  }[];
}

export interface AddFamilyMemberResponse {
  message: string;
  pet: PetResponse;
}

export interface RemoveFamilyMemberRequest {
  relation: FamilyRelation;
  siblingDid?: string;
}

// Create a new pet
export async function createPet(
  data: CreatePetRequest
): Promise<CreatePetResponse> {
  const response = await apiClient.post<CreatePetResponse>('/pets', data);
  return response.data;
}

// Get all pets for the authenticated user
export async function getPets(): Promise<GetPetsResponse> {
  const response = await apiClient.get<GetPetsResponse>('/pets');
  return response.data;
}

// Pending pet invitations (invited as co-owner/caretaker but not yet accepted)
export interface PendingPetInvitation {
  petId: string;
  petName: string;
  inviterName: string;
}

export interface GetPendingPetInvitationsResponse {
  invitations: PendingPetInvitation[];
}

export async function getPendingPetInvitations(): Promise<GetPendingPetInvitationsResponse> {
  const response = await apiClient.get<GetPendingPetInvitationsResponse>(
    '/pets/invitations/pending'
  );
  return response.data;
}

export async function acceptPetInvitation(petId: string): Promise<{
  message: string;
  pet: { id: string; name: string };
}> {
  const response = await apiClient.post<{
    message: string;
    pet: { id: string; name: string };
  }>('/pets/invitations/accept', { petId });
  return response.data;
}

export async function declinePetInvitation(
  petId: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/pets/invitations/decline',
    { petId }
  );
  return response.data;
}

export async function removePetOwner(
  petId: string,
  email: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/pets/${petId}/owners/remove`,
    { email }
  );
  return response.data;
}

// Get a single pet by ID
export async function getPetById(id: string): Promise<{ pet: PetResponse }> {
  const response = await apiClient.get<{ pet: PetResponse }>(`/pets/${id}`);
  return response.data;
}

// Update a pet (may include invitation email failure info when adding owners/caretakers)
export interface UpdatePetResponse {
  message: string;
  pet: PetResponse;
  invitationEmailFailures?: string[];
  invitationEmailError?: string;
  invitationEmailWarning?: string;
}

export async function updatePet(
  id: string,
  data: Partial<CreatePetRequest>
): Promise<UpdatePetResponse> {
  const response = await apiClient.put<UpdatePetResponse>(`/pets/${id}`, data);
  return response.data;
}

// Update pet DID
export async function updatePetDid(
  id: string,
  did: string
): Promise<UpdatePetDidResponse> {
  const response = await apiClient.patch<UpdatePetDidResponse>(
    `/pets/${id}/did`,
    { did }
  );
  return response.data;
}

export async function addFamilyMember(
  petId: string,
  data: AddFamilyMemberRequest
): Promise<AddFamilyMemberResponse> {
  const response = await apiClient.post<AddFamilyMemberResponse>(
    `/pets/${petId}/family-member`,
    data
  );
  return response.data;
}

export async function searchPets(
  data: SearchPetsRequest
): Promise<SearchPetsResponse> {
  const response = await apiClient.post<SearchPetsResponse>(
    '/pets/search',
    data
  );
  return response.data;
}

export async function removeFamilyMember(
  petId: string,
  data: RemoveFamilyMemberRequest
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/family-member`,
    { data }
  );
  return response.data;
}

// ============================================================================
// BIRTH / LITTER REGISTRATION
// ============================================================================

export type ParentRole = 'mother' | 'father';

export interface BirthEvent {
  id: string;
  parentPrimaryDid: string;
  parentSecondaryDid?: string;
  birthDate: string;
  litterSize: number;
  location?: string;
  notes?: string;
  createdAt: string;
}

export interface CreateBirthEventRequest {
  birthDate: string;
  litterSize: number;
  parentRole: ParentRole;
  parentPrimaryDid: string;
  parentSecondaryDid?: string;
  location?: string;
  notes?: string;
}

export interface CreatePuppyInput {
  name: string;
  gender: 'male' | 'female';
  color?: string;
  notes?: string;
  ownershipStatus: 'active' | 'pending_transfer';
}

export interface CreateBirthEventResponse {
  message: string;
  birthEvent: BirthEvent;
  puppies: PetResponse[];
}

export interface GetBirthEventsResponse {
  birthEvents: BirthEvent[];
}

// Search pet by DID (for other parent lookup)
export async function searchPetByDid(did: string): Promise<SearchPetsResponse> {
  return searchPets({ did });
}

// Create birth event and puppies (backend may support single endpoint; otherwise we create pets and link)
export async function createBirthEventAndPuppies(
  parentPetId: string,
  parentDid: string,
  request: CreateBirthEventRequest,
  puppies: CreatePuppyInput[],
  species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other',
  breed: string
): Promise<CreateBirthEventResponse> {
  const motherDid =
    request.parentRole === 'mother'
      ? request.parentPrimaryDid
      : request.parentSecondaryDid;
  const fatherDid =
    request.parentRole === 'father'
      ? request.parentPrimaryDid
      : request.parentSecondaryDid;

  const createdPuppies: PetResponse[] = [];
  for (let i = 0; i < puppies.length; i++) {
    const p = puppies[i];
    const petData: CreatePetRequest = {
      name: p.name,
      species,
      breed,
      dateOfBirth: request.birthDate,
      gender: p.gender,
      motherDid: motherDid || undefined,
      fatherDid: fatherDid || undefined,
      color: p.color,
      ownershipStatus: p.ownershipStatus,
    };
    const res = await createPet(petData);
    if (res.pet) createdPuppies.push(res.pet as PetResponse);
  }

  const birthEvent: BirthEvent = {
    id: `be-${Date.now()}`,
    parentPrimaryDid: request.parentPrimaryDid,
    parentSecondaryDid: request.parentSecondaryDid,
    birthDate: request.birthDate,
    litterSize: request.litterSize,
    location: request.location,
    notes: request.notes,
    createdAt: new Date().toISOString(),
  };

  return {
    message: 'Birth registered and puppies created.',
    birthEvent,
    puppies: createdPuppies,
  };
}

// Get birth events for a pet (by parent DID). Backend may add GET /pets/:id/birth-events
export async function getBirthEventsForPet(
  petId: string
): Promise<GetBirthEventsResponse> {
  try {
    const response = await apiClient.get<GetBirthEventsResponse>(
      `/pets/${petId}/birth-events`
    );
    return response.data;
  } catch {
    return { birthEvents: [] };
  }
}

// Transfer ownership of a puppy (stub – backend may add POST /pets/:id/transfer-ownership)
export interface TransferOwnershipRequest {
  newOwnerEmail?: string;
  newOwnerDid?: string;
}

export interface TransferOwnershipResponse {
  message: string;
  pet: PetResponse;
}

export async function transferOwnership(
  petId: string,
  data: TransferOwnershipRequest
): Promise<TransferOwnershipResponse> {
  const response = await apiClient.post<TransferOwnershipResponse>(
    `/pets/${petId}/transfer-ownership`,
    data
  );
  return response.data;
}

// Microchip types
export interface LinkMicrochipRequest {
  chipNumber: string;
  standard?: 'ISO' | 'AVID' | 'Other';
  implantDate?: string;
  implantLocation?: string;
  linkToDID: boolean;
  visibility: 'only-me' | 'professionals' | 'emergency';
}

export interface LinkMicrochipResponse {
  message: string;
  microchip: MicrochipDataResponse;
}

// Link or update microchip data
export async function linkMicrochip(
  petId: string,
  data: LinkMicrochipRequest
): Promise<LinkMicrochipResponse> {
  const response = await apiClient.put<LinkMicrochipResponse>(
    `/pets/${petId}/microchip`,
    data
  );
  return response.data;
}

// Update microchip settings (visibility, linkToDID)
export async function updateMicrochipSettings(
  petId: string,
  data: Partial<LinkMicrochipRequest>
): Promise<LinkMicrochipResponse> {
  const response = await apiClient.put<LinkMicrochipResponse>(
    `/pets/${petId}/microchip`,
    data
  );
  return response.data;
}

// Unlink microchip from pet
export async function unlinkMicrochip(
  petId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/microchip`
  );
  return response.data;
}

// DID Status types
export interface RevokeDidResponse {
  message: string;
  pet: {
    id: string;
    did: string;
    didStatus: DidStatus;
    didRevokedAt: string;
    name: string;
  };
}

export interface RestoreDidResponse {
  message: string;
  pet: {
    id: string;
    did: string;
    didStatus: DidStatus;
    name: string;
  };
}

// Revoke a pet's DID
export async function revokeDid(petId: string): Promise<RevokeDidResponse> {
  const response = await apiClient.post<RevokeDidResponse>(
    `/pets/${petId}/did/revoke`
  );
  return response.data;
}

// Restore a pet's DID
export async function restoreDid(petId: string): Promise<RestoreDidResponse> {
  const response = await apiClient.post<RestoreDidResponse>(
    `/pets/${petId}/did/restore`
  );
  return response.data;
}

// Fetch a file from R2 via the backend and return a blob URL for display.
export async function getSignedUrl(
  key: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<{ url: string; filename?: string; contentType?: string }> {
  const encodedKey = encodeURIComponent(key);
  const response = await apiClient.get(`/documents/${encodedKey}/download`, {
    params: { disposition },
    responseType: 'blob',
  });

  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const contentType = blob.type || undefined;

  const cdHeader = response.headers?.['content-disposition'] as string | undefined;
  let filename: string | undefined;
  if (cdHeader) {
    const match = cdHeader.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }

  return { url, filename, contentType };
}

// Initialize a photo upload for a pet profile photo.
// Returns the R2 key; the caller uploads via PUT /pets/photo-upload/:key.
export async function initPetPhotoUpload(data: {
  petId: string;
  filename: string;
  contentType?: string;
}): Promise<{ key: string }> {
  const response = await apiClient.post<{ key: string }>(
    '/pets/photo-upload-init',
    data,
  );

  const key = response.data?.key;
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid pet photo upload init response from server');
  }

  return { key };
}

// Upload a pet photo file to R2 via the backend proxy endpoint.
export async function uploadPetPhoto(
  key: string,
  file: File | Blob,
  contentType: string,
): Promise<void> {
  await apiClient.put(
    `/pets/photo-upload/${encodeURIComponent(key)}`,
    file,
    { headers: { 'Content-Type': contentType } },
  );
}

function unwrapApiPayload(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const asRecord = raw as Record<string, unknown>;

  if (typeof asRecord.body === 'string') {
    try {
      return JSON.parse(asRecord.body);
    } catch {
      // ignore malformed body wrapper and continue
    }
  } else if (asRecord.body && typeof asRecord.body === 'object') {
    return asRecord.body;
  }

  if (asRecord.data && typeof asRecord.data === 'object') {
    return asRecord.data;
  }

  return raw;
}

function normalizePhysicalMetric(raw: unknown): PhysicalMetric {
  const item = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const updatedByType = item.updatedByType;
  return {
    id: typeof item.id === 'string' ? item.id : `metric-${Date.now()}`,
    type:
      item.type === 'weight' ||
      item.type === 'height' ||
      item.type === 'bodyCondition'
        ? item.type
        : 'weight',
    value: Number(item.value ?? 0),
    unit: typeof item.unit === 'string' ? item.unit : '',
    date:
      typeof item.date === 'string' && item.date
        ? item.date
        : new Date().toISOString(),
    updatedBy: typeof item.updatedBy === 'string' ? item.updatedBy : '',
    updatedByType:
      updatedByType === 'owner' ||
      updatedByType === 'vet' ||
      updatedByType === 'groomer'
        ? updatedByType
        : 'owner',
    verified: Boolean(item.verified),
    notes: typeof item.notes === 'string' ? item.notes : undefined,
    txSignature:
      typeof item.txSignature === 'string' ? item.txSignature : undefined,
    createdAt:
      typeof item.createdAt === 'string' && item.createdAt
        ? item.createdAt
        : new Date().toISOString(),
  };
}

function normalizeVaccination(raw: unknown): Vaccination {
  const item = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    id: typeof item.id === 'string' ? item.id : `vacc-${Date.now()}`,
    vaccineName: typeof item.vaccineName === 'string' ? item.vaccineName : '',
    dateAdministered:
      typeof item.dateAdministered === 'string' && item.dateAdministered
        ? item.dateAdministered
        : new Date().toISOString(),
    expiryDate:
      typeof item.expiryDate === 'string' && item.expiryDate
        ? item.expiryDate
        : new Date().toISOString(),
    boosterDate:
      typeof item.boosterDate === 'string' ? item.boosterDate : undefined,
    administeredBy:
      typeof item.administeredBy === 'string' ? item.administeredBy : '',
    clinicName: typeof item.clinicName === 'string' ? item.clinicName : '',
    batchNumber: typeof item.batchNumber === 'string' ? item.batchNumber : '',
    verified: Boolean(item.verified),
    credentialIssued: Boolean(item.credentialIssued),
    documentHash:
      typeof item.documentHash === 'string'
        ? item.documentHash
        : typeof (item as Record<string, unknown>).document_hash === 'string'
          ? ((item as Record<string, unknown>).document_hash as string)
          : undefined,
    notes: typeof item.notes === 'string' ? item.notes : undefined,
    txSignature:
      typeof item.txSignature === 'string' ? item.txSignature : undefined,
    createdAt:
      typeof item.createdAt === 'string' && item.createdAt
        ? item.createdAt
        : new Date().toISOString(),
    updatedAt:
      typeof item.updatedAt === 'string' && item.updatedAt
        ? item.updatedAt
        : new Date().toISOString(),
  };
}

function normalizeGroomingRecord(raw: unknown): GroomingRecord {
  const item = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    id: typeof item.id === 'string' ? item.id : `groom-${Date.now()}`,
    serviceType:
      typeof item.serviceType === 'string'
        ? (item.serviceType as GroomingServiceType)
        : 'Other',
    serviceDate:
      typeof item.serviceDate === 'string' && item.serviceDate
        ? item.serviceDate
        : new Date().toISOString(),
    groomerName: typeof item.groomerName === 'string' ? item.groomerName : '',
    salonName: typeof item.salonName === 'string' ? item.salonName : '',
    servicesProvided: Array.isArray(item.servicesProvided)
      ? (item.servicesProvided as GroomingService[])
      : [],
    coatCondition:
      typeof item.coatCondition === 'string' ? item.coatCondition : '',
    behavioralObservations:
      typeof item.behavioralObservations === 'string'
        ? item.behavioralObservations
        : '',
    groomerNotes:
      typeof item.groomerNotes === 'string' ? item.groomerNotes : undefined,
    verified: Boolean(item.verified),
    txSignature:
      typeof item.txSignature === 'string' ? item.txSignature : undefined,
    documentHash:
      typeof item.documentHash === 'string'
        ? item.documentHash
        : typeof (item as Record<string, unknown>).document_hash === 'string'
          ? ((item as Record<string, unknown>).document_hash as string)
          : undefined,
    createdAt:
      typeof item.createdAt === 'string' && item.createdAt
        ? item.createdAt
        : new Date().toISOString(),
    updatedAt:
      typeof item.updatedAt === 'string' && item.updatedAt
        ? item.updatedAt
        : new Date().toISOString(),
  };
}

function normalizeMedicalUpdate(raw: unknown): MedicalUpdate {
  const item = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const status = item.status;
  const rawMeds = item.medications;
  const medications = Array.isArray(rawMeds)
    ? rawMeds.filter((m): m is string => typeof m === 'string')
    : typeof rawMeds === 'string'
      ? rawMeds
          .split('\n')
          .map((m) => m.trim())
          .filter(Boolean)
      : undefined;
  return {
    id: typeof item.id === 'string' ? item.id : `med-${Date.now()}`,
    updateType:
      item.updateType === 'Diagnosis' ||
      item.updateType === 'Treatment' ||
      item.updateType === 'Prescription' ||
      item.updateType === 'Recovery'
        ? item.updateType
        : 'Diagnosis',
    title: typeof item.title === 'string' ? item.title : '',
    date:
      typeof item.date === 'string' && item.date
        ? item.date
        : new Date().toISOString(),
    veterinarian:
      typeof item.veterinarian === 'string' ? item.veterinarian : '',
    clinicName: typeof item.clinicName === 'string' ? item.clinicName : '',
    description: typeof item.description === 'string' ? item.description : '',
    medications,
    status:
      status === 'Active' || status === 'Ongoing' || status === 'Resolved'
        ? status
        : 'Active',
    followUpDate:
      typeof item.followUpDate === 'string' ? item.followUpDate : undefined,
    verified: Boolean(item.verified),
    documentHash:
      typeof item.documentHash === 'string'
        ? item.documentHash
        : typeof (item as Record<string, unknown>).document_hash === 'string'
          ? ((item as Record<string, unknown>).document_hash as string)
          : undefined,
    txSignature:
      typeof item.txSignature === 'string' ? item.txSignature : undefined,
    createdAt:
      typeof item.createdAt === 'string' && item.createdAt
        ? item.createdAt
        : new Date().toISOString(),
    updatedAt:
      typeof item.updatedAt === 'string' && item.updatedAt
        ? item.updatedAt
        : new Date().toISOString(),
  };
}

// ============================================================================
// PHYSICAL METRICS
// ============================================================================

export type MetricType = 'weight' | 'height' | 'bodyCondition';
export type UpdaterType = 'owner' | 'vet' | 'groomer';

export interface PhysicalMetric {
  id: string;
  type: MetricType;
  value: number;
  unit: string;
  date: string;
  updatedBy: string;
  updatedByType: UpdaterType;
  verified: boolean;
  notes?: string;
  txSignature?: string;
  createdAt: string;
}

export interface CreatePhysicalMetricRequest {
  type: MetricType;
  value: number;
  unit: string;
  date: string;
  updatedBy: string;
  updatedByType: UpdaterType;
  verified?: boolean;
  notes?: string;
}

export interface CreatePhysicalMetricResponse {
  message: string;
  metric: PhysicalMetric;
}

export interface GetPhysicalMetricsResponse {
  metrics: PhysicalMetric[];
}

// Create a new physical metric
export async function createPhysicalMetric(
  petId: string,
  data: CreatePhysicalMetricRequest
): Promise<CreatePhysicalMetricResponse> {
  const response = await apiClient.post<CreatePhysicalMetricResponse>(
    `/pets/${petId}/physical-metrics`,
    data
  );
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'metric' in payload &&
    payload.metric
  ) {
    const metric = (payload as { metric: unknown }).metric;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Physical metric created successfully',
      metric: normalizePhysicalMetric(metric),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Physical metric created successfully',
      metric: normalizePhysicalMetric(payload),
    };
  }
  throw new Error('Invalid create physical metric response');
}

// Get all physical metrics for a pet
export async function getPhysicalMetrics(
  petId: string
): Promise<GetPhysicalMetricsResponse> {
  const response = await apiClient.get<GetPhysicalMetricsResponse>(
    `/pets/${petId}/physical-metrics`
  );
  const payload = unwrapApiPayload(response.data);
  if (Array.isArray(payload)) {
    return { metrics: payload.map(normalizePhysicalMetric) };
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { metrics?: unknown }).metrics)
  ) {
    return {
      metrics: ((payload as { metrics: unknown[] }).metrics || []).map(
        normalizePhysicalMetric
      ),
    };
  }
  return { metrics: [] };
}

// Get a single physical metric by ID
export async function getPhysicalMetricById(
  petId: string,
  metricId: string
): Promise<{ metric: PhysicalMetric }> {
  const response = await apiClient.get<{ metric: PhysicalMetric }>(
    `/pets/${petId}/physical-metrics/${metricId}`
  );
  return response.data;
}

// Delete a physical metric
export async function deletePhysicalMetric(
  petId: string,
  metricId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/physical-metrics/${metricId}`
  );
  return response.data;
}

// ============================================================================
// VACCINATIONS
// ============================================================================

export interface Vaccination {
  id: string;
  vaccineName: string;
  dateAdministered: string;
  expiryDate: string;
  boosterDate?: string;
  administeredBy: string;
  clinicName: string;
  batchNumber: string;
  verified: boolean;
  credentialIssued: boolean;
  documentHash?: string;
  notes?: string;
  txSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVaccinationRequest {
  vaccineName: string;
  dateAdministered: string;
  expiryDate: string;
  boosterDate?: string;
  administeredBy: string;
  clinicName: string;
  batchNumber: string;
  verified?: boolean;
  notes?: string;
}

export interface UpdateVaccinationRequest {
  vaccineName?: string;
  dateAdministered?: string;
  expiryDate?: string;
  boosterDate?: string | null;
  administeredBy?: string;
  clinicName?: string;
  batchNumber?: string;
  verified?: boolean;
  notes?: string | null;
  documentHash?: string | null;
}

export interface CreateVaccinationResponse {
  message: string;
  vaccination: Vaccination;
}

export interface GetVaccinationsResponse {
  vaccinations: Vaccination[];
}

// Create a new vaccination
export async function createVaccination(
  petId: string,
  data: CreateVaccinationRequest
): Promise<CreateVaccinationResponse> {
  const response = await apiClient.post<CreateVaccinationResponse>(
    `/pets/${petId}/vaccinations`,
    data
  );
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'vaccination' in payload &&
    payload.vaccination
  ) {
    const vaccination = (payload as { vaccination: unknown }).vaccination;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Vaccination created successfully',
      vaccination: normalizeVaccination(vaccination),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Vaccination created successfully',
      vaccination: normalizeVaccination(payload),
    };
  }
  throw new Error('Invalid create vaccination response');
}

// Get all vaccinations for a pet
export async function getVaccinations(
  petId: string
): Promise<GetVaccinationsResponse> {
  const response = await apiClient.get<GetVaccinationsResponse>(
    `/pets/${petId}/vaccinations`
  );
  const payload = unwrapApiPayload(response.data);
  if (Array.isArray(payload)) {
    return { vaccinations: payload.map(normalizeVaccination) };
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { vaccinations?: unknown }).vaccinations)
  ) {
    return {
      vaccinations: (
        (payload as { vaccinations: unknown[] }).vaccinations || []
      ).map(normalizeVaccination),
    };
  }
  return { vaccinations: [] };
}

// Get a single vaccination by ID
export async function getVaccinationById(
  petId: string,
  vaccinationId: string
): Promise<{ vaccination: Vaccination }> {
  const response = await apiClient.get<{ vaccination: Vaccination }>(
    `/pets/${petId}/vaccinations/${vaccinationId}`
  );
  return response.data;
}

/** Send documentHash as both camelCase and snake_case so backend can accept either. */
function withDocumentKeyPayload<T extends Record<string, unknown>>(
  data: T & { documentHash?: string | null }
): T & { document_hash?: string } {
  if (data.documentHash == null || data.documentHash === '') return data;
  return { ...data, document_hash: data.documentHash } as T & {
    document_hash?: string;
  };
}

// Update a vaccination
export async function updateVaccination(
  petId: string,
  vaccinationId: string,
  data: UpdateVaccinationRequest
): Promise<{ message: string; vaccination: Vaccination }> {
  const body = withDocumentKeyPayload(
    data as UpdateVaccinationRequest & Record<string, unknown>
  );
  const response = await apiClient.put<{
    message: string;
    vaccination: Vaccination;
  }>(`/pets/${petId}/vaccinations/${vaccinationId}`, body);
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'vaccination' in payload &&
    payload.vaccination
  ) {
    const vaccination = (payload as { vaccination: unknown }).vaccination;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Vaccination updated successfully',
      vaccination: normalizeVaccination(vaccination),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Vaccination updated successfully',
      vaccination: normalizeVaccination(payload),
    };
  }
  return {
    message: 'Vaccination updated successfully',
    vaccination: {
      id: vaccinationId,
      documentHash: data.documentHash ?? undefined,
    } as Vaccination,
  };
}

// Delete a vaccination
export async function deleteVaccination(
  petId: string,
  vaccinationId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/vaccinations/${vaccinationId}`
  );
  return response.data;
}

// ============================================================================
// GROOMING RECORDS
// ============================================================================

export type GroomingServiceType =
  | 'Full Grooming Service'
  | 'Basic Bath & Brush'
  | 'Nail Trim Only'
  | 'De-shedding Treatment'
  | 'Puppy/Kitten First Groom'
  | 'Other';

export type GroomingService =
  | 'Bath'
  | 'Haircut'
  | 'Nail Trim'
  | 'Ear Cleaning'
  | 'Brush'
  | 'De-shedding'
  | 'Teeth Brushing'
  | 'Anal Gland Expression';

export interface GroomingRecord {
  id: string;
  serviceType: GroomingServiceType;
  serviceDate: string;
  groomerName: string;
  salonName: string;
  servicesProvided: GroomingService[];
  coatCondition: string;
  behavioralObservations: string;
  groomerNotes?: string;
  verified: boolean;
  txSignature?: string;
  documentHash?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroomingRecordRequest {
  serviceType: GroomingServiceType;
  serviceDate: string;
  groomerName: string;
  salonName: string;
  servicesProvided: GroomingService[];
  coatCondition: string;
  behavioralObservations: string;
  groomerNotes?: string;
  verified?: boolean;
}

export interface UpdateGroomingRecordRequest {
  serviceType?: GroomingServiceType;
  serviceDate?: string;
  groomerName?: string;
  salonName?: string;
  servicesProvided?: GroomingService[];
  coatCondition?: string;
  behavioralObservations?: string;
  groomerNotes?: string | null;
  verified?: boolean;
  documentHash?: string | null;
}

export interface CreateGroomingRecordResponse {
  message: string;
  groomingRecord: GroomingRecord;
}

export interface GetGroomingRecordsResponse {
  groomingRecords: GroomingRecord[];
}

// Create a new grooming record
export async function createGroomingRecord(
  petId: string,
  data: CreateGroomingRecordRequest
): Promise<CreateGroomingRecordResponse> {
  const response = await apiClient.post<CreateGroomingRecordResponse>(
    `/pets/${petId}/grooming`,
    data
  );
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'groomingRecord' in payload &&
    payload.groomingRecord
  ) {
    const groomingRecord = (payload as { groomingRecord: unknown })
      .groomingRecord;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Grooming record created successfully',
      groomingRecord: normalizeGroomingRecord(groomingRecord),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Grooming record created successfully',
      groomingRecord: normalizeGroomingRecord(payload),
    };
  }
  throw new Error('Invalid create grooming record response');
}

// Get all grooming records for a pet
export async function getGroomingRecords(
  petId: string
): Promise<GetGroomingRecordsResponse> {
  const response = await apiClient.get<GetGroomingRecordsResponse>(
    `/pets/${petId}/grooming`
  );
  const payload = unwrapApiPayload(response.data);
  if (Array.isArray(payload)) {
    return { groomingRecords: payload.map(normalizeGroomingRecord) };
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { groomingRecords?: unknown }).groomingRecords)
  ) {
    return {
      groomingRecords: (
        (payload as { groomingRecords: unknown[] }).groomingRecords || []
      ).map(normalizeGroomingRecord),
    };
  }
  return { groomingRecords: [] };
}

// Get a single grooming record by ID
export async function getGroomingRecordById(
  petId: string,
  groomingId: string
): Promise<{ groomingRecord: GroomingRecord }> {
  const response = await apiClient.get<{ groomingRecord: GroomingRecord }>(
    `/pets/${petId}/grooming/${groomingId}`
  );
  return response.data;
}

// Update a grooming record
export async function updateGroomingRecord(
  petId: string,
  groomingId: string,
  data: UpdateGroomingRecordRequest
): Promise<{ message: string; groomingRecord: GroomingRecord }> {
  const body = withDocumentKeyPayload(
    data as UpdateGroomingRecordRequest & Record<string, unknown>
  );
  const response = await apiClient.put<{
    message: string;
    groomingRecord: GroomingRecord;
  }>(`/pets/${petId}/grooming/${groomingId}`, body);
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'groomingRecord' in payload &&
    payload.groomingRecord
  ) {
    const groomingRecord = (payload as { groomingRecord: unknown })
      .groomingRecord;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Grooming record updated successfully',
      groomingRecord: normalizeGroomingRecord(groomingRecord),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Grooming record updated successfully',
      groomingRecord: normalizeGroomingRecord(payload),
    };
  }
  return {
    message: 'Grooming record updated successfully',
    groomingRecord: {
      id: groomingId,
      documentHash: data.documentHash ?? undefined,
    } as GroomingRecord,
  };
}

// Delete a grooming record
export async function deleteGroomingRecord(
  petId: string,
  groomingId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/grooming/${groomingId}`
  );
  return response.data;
}

// ============================================================================
// MEDICAL UPDATES
// ============================================================================

export type MedicalUpdateType =
  | 'Diagnosis'
  | 'Treatment'
  | 'Prescription'
  | 'Recovery';

export type MedicalUpdateStatus = 'Active' | 'Ongoing' | 'Resolved';

export interface MedicalUpdate {
  id: string;
  updateType: MedicalUpdateType;
  title: string;
  date: string;
  veterinarian: string;
  clinicName: string;
  description: string;
  medications?: string[];
  status: MedicalUpdateStatus;
  followUpDate?: string;
  verified: boolean;
  documentHash?: string;
  txSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMedicalUpdateRequest {
  updateType: MedicalUpdateType;
  title: string;
  date: string;
  veterinarian: string;
  clinicName: string;
  description: string;
  medications?: string[];
  status: MedicalUpdateStatus;
  followUpDate?: string;
  verified?: boolean;
}

export interface UpdateMedicalUpdateRequest {
  updateType?: MedicalUpdateType;
  title?: string;
  date?: string;
  veterinarian?: string;
  clinicName?: string;
  description?: string;
  medications?: string[] | null;
  status?: MedicalUpdateStatus;
  followUpDate?: string | null;
  verified?: boolean;
  documentHash?: string | null;
}

export interface CreateMedicalUpdateResponse {
  message: string;
  medicalUpdate: MedicalUpdate;
}

export interface GetMedicalUpdatesResponse {
  medicalUpdates: MedicalUpdate[];
}

// Create a new medical update
export async function createMedicalUpdate(
  petId: string,
  data: CreateMedicalUpdateRequest
): Promise<CreateMedicalUpdateResponse> {
  const response = await apiClient.post<CreateMedicalUpdateResponse>(
    `/pets/${petId}/medical-updates`,
    data
  );
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'medicalUpdate' in payload &&
    payload.medicalUpdate
  ) {
    const medicalUpdate = (payload as { medicalUpdate: unknown }).medicalUpdate;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Medical update created successfully',
      medicalUpdate: normalizeMedicalUpdate(medicalUpdate),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Medical update created successfully',
      medicalUpdate: normalizeMedicalUpdate(payload),
    };
  }
  throw new Error('Invalid create medical update response');
}

// Get all medical updates for a pet
export async function getMedicalUpdates(
  petId: string
): Promise<GetMedicalUpdatesResponse> {
  const response = await apiClient.get<GetMedicalUpdatesResponse>(
    `/pets/${petId}/medical-updates`
  );
  const payload = unwrapApiPayload(response.data);
  if (Array.isArray(payload)) {
    return { medicalUpdates: payload.map(normalizeMedicalUpdate) };
  }
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { medicalUpdates?: unknown }).medicalUpdates)
  ) {
    return {
      medicalUpdates: (
        (payload as { medicalUpdates: unknown[] }).medicalUpdates || []
      ).map(normalizeMedicalUpdate),
    };
  }
  return { medicalUpdates: [] };
}

// Get a single medical update by ID
export async function getMedicalUpdateById(
  petId: string,
  medicalUpdateId: string
): Promise<{ medicalUpdate: MedicalUpdate }> {
  const response = await apiClient.get<{ medicalUpdate: MedicalUpdate }>(
    `/pets/${petId}/medical-updates/${medicalUpdateId}`
  );
  return response.data;
}

// Update a medical update
export async function updateMedicalUpdate(
  petId: string,
  medicalUpdateId: string,
  data: UpdateMedicalUpdateRequest
): Promise<{ message: string; medicalUpdate: MedicalUpdate }> {
  const body = withDocumentKeyPayload(
    data as UpdateMedicalUpdateRequest & Record<string, unknown>
  );
  const response = await apiClient.put<{
    message: string;
    medicalUpdate: MedicalUpdate;
  }>(`/pets/${petId}/medical-updates/${medicalUpdateId}`, body);
  const payload = unwrapApiPayload(response.data);
  if (
    payload &&
    typeof payload === 'object' &&
    'medicalUpdate' in payload &&
    payload.medicalUpdate
  ) {
    const medicalUpdate = (payload as { medicalUpdate: unknown }).medicalUpdate;
    return {
      message:
        typeof (payload as { message?: unknown }).message === 'string'
          ? ((payload as { message: string }).message as string)
          : 'Medical update updated successfully',
      medicalUpdate: normalizeMedicalUpdate(medicalUpdate),
    };
  }
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return {
      message: 'Medical update updated successfully',
      medicalUpdate: normalizeMedicalUpdate(payload),
    };
  }
  return {
    message: 'Medical update updated successfully',
    medicalUpdate: {
      id: medicalUpdateId,
      documentHash: data.documentHash ?? undefined,
    } as MedicalUpdate,
  };
}

// Delete a medical update
export async function deleteMedicalUpdate(
  petId: string,
  medicalUpdateId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pets/${petId}/medical-updates/${medicalUpdateId}`
  );
  return response.data;
}

// ============================================================================
// PET DRAFT
// ============================================================================

export interface PetDraft {
  petName: string;
  species: string;
  breed: string;
  dateOfBirth: string;
  approximateAge: string;
  gender: string;
  neuteredSpayed: string;
  color: string;
  motherDID: string;
  fatherDID: string;
  country: string;
  city: string;
  customCity: string;
  primaryOwner: string;
  secondaryOwners: string[];
  shareHealthData: boolean;
  shareGroomingNotes: boolean;
  emergencyAccess: boolean;
  medicalConditions: string;
  allergies: string;
  currentMedications: string;
  dietaryRequirements: string;
  vetClinic: string;
  temperament: string[];
  groomingTolerance: string;
  trainingLevel: string;
  exerciseNeeds: string;
  boardingSuitability: string;
  currentStep: number;
  savedAt?: string;
}

// Save pet draft
export async function savePetDraft(
  data: Partial<PetDraft>
): Promise<{ message: string; draft: PetDraft }> {
  const response = await apiClient.put<{ message: string; draft: PetDraft }>(
    '/pets/draft',
    data
  );
  return response.data;
}

// Get pet draft
export async function getPetDraft(): Promise<{ draft: PetDraft | null }> {
  const response = await apiClient.get<{ draft: PetDraft | null }>(
    '/pets/draft'
  );
  return response.data;
}

// Delete pet draft
export async function deletePetDraft(): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>('/pets/draft');
  return response.data;
}

// ============================================================================
// LOST PET
// ============================================================================

export interface ReportLostRequest {
  description?: string;
  location?: string;
}

export interface ReportLostResponse {
  message: string;
  pet: {
    id: string;
    name: string;
    lostStatus: LostStatus;
    lostAt: string;
    lostDescription?: string;
    lostLocation?: string;
  };
}

export interface MarkFoundResponse {
  message: string;
  pet: {
    id: string;
    name: string;
    lostStatus: LostStatus;
    lostAt?: string;
    recoveredAt: string;
  };
}

// Report a pet as lost
export async function reportLost(
  petId: string,
  data: ReportLostRequest = {}
): Promise<ReportLostResponse> {
  const response = await apiClient.post<ReportLostResponse>(
    `/pets/${petId}/lost`,
    data
  );
  return response.data;
}

// Mark a pet as found/recovered
export async function markFound(petId: string): Promise<MarkFoundResponse> {
  const response = await apiClient.post<MarkFoundResponse>(
    `/pets/${petId}/found`
  );
  return response.data;
}
